<template>
  <div class="px-6 py-12 space-y-12 min-h-screen font-light select-none text-left transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-gray-50 text-gray-700'">

    <div class="text-center space-y-4 border-b pb-10 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-gray-200'">
      <h1 class="text-2xl font-medium uppercase tracking-widest transition-colors duration-200"
        :class="isDark ? 'text-white' : 'text-gray-900'">Abonnements & Licences Établissements</h1>
      <p class="text-xs max-w-xl mx-auto leading-relaxed transition-colors duration-200"
        :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
        Sélectionnez la configuration adaptée à votre activité. Des outils performants conçus pour les indépendants comme pour les salons à forte équipe.
      </p>

      <!-- Toggle between Monthly and Lifetime -->
      <div class="flex flex-col items-center gap-3 pt-4">
        <div class="flex items-center gap-2">
          <button
            @click="pricingMode = 'monthly'"
            class="px-6 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all"
            :class="pricingMode === 'monthly'
              ? (isDark ? 'bg-white text-black' : 'bg-amber-600 text-white')
              : (isDark ? 'bg-zinc-900 text-zinc-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900')"
          >
            Mensuel
          </button>
          <button
            @click="!isLifetimeBlocked && (pricingMode = 'lifetime')"
            :disabled="isLifetimeBlocked"
            class="px-6 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative"
            :class="pricingMode === 'lifetime'
              ? (isDark ? 'bg-white text-black' : 'bg-amber-600 text-white')
              : (isDark ? 'bg-zinc-900 text-zinc-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900')"
            :title="isLifetimeBlocked ? 'Non disponible pour les abonnements mensuels actifs' : ''"
          >
            <i class="fas fa-infinity mr-1"></i>
            À vie (One-Shot)
            <i v-if="isLifetimeBlocked" class="fas fa-lock ml-1 text-[10px]"></i>
          </button>
        </div>

        <!-- Warning message for blocked lifetime -->
        <div v-if="isLifetimeBlocked" class="px-4 py-2 rounded-lg border text-xs flex items-center gap-2 max-w-lg"
          :class="isDark ? 'bg-amber-950/20 border-amber-900/40 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700'">
          <i class="fas fa-info-circle"></i>
          <span>Les offres à vie ne sont pas accessibles avec un abonnement mensuel actif. Annulez d'abord votre abonnement mensuel si vous souhaitez passer à une licence à vie.</span>
        </div>
      </div>
    </div>

    <div v-if="pricingMode === 'lifetime' && (lifetimeSolo.isAvailable || lifetimeEquipe.isAvailable || lifetimeSalon.isAvailable)" class="max-w-5xl mx-auto">
      <div class="border-2 rounded-2xl p-8 relative overflow-hidden transition-colors duration-200"
        :class="isDark ? 'bg-gradient-to-br from-zinc-900/60 to-zinc-950/40 border-[#D5CCBC]/20' : 'bg-gradient-to-br from-amber-50/50 to-white border-amber-300/40'">

        <div class="absolute -top-32 -right-32 w-96 h-96 blur-3xl -z-0 animate-pulse"
          :class="isDark ? 'bg-[#D5CCBC]/5' : 'bg-amber-400/10'"></div>

        <div class="relative z-10 text-center space-y-4">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full transition-colors duration-200"
            :class="isDark ? 'bg-zinc-950/60 border border-[#D5CCBC]/30' : 'bg-white border border-amber-400/40'">
            <span class="text-[10px] font-mono uppercase tracking-widest font-bold transition-colors duration-200"
              :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-700'">
              ⚡ Offre Fondateurs — Seulement 100 Places Disponibles
            </span>
          </div>

          <h2 class="text-2xl font-medium uppercase tracking-wider transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-gray-900'">
            Accès à Vie — Tarification Évolutive par Palier
          </h2>

          <p class="text-sm max-w-2xl mx-auto leading-relaxed transition-colors duration-200"
            :class="isDark ? 'text-zinc-400' : 'text-gray-700'">
            Devenez membre fondateur avec un <strong class="transition-colors duration-200" :class="isDark ? 'text-white' : 'text-gray-900'">paiement unique</strong>.
            Plus vous rejoignez notre réseau tôt, plus l'avantage est grand. Le prix s'ajuste automatiquement tous les 30 clients.
          </p>

          <div class="pt-4 pb-2">
            <div class="inline-flex items-center gap-6 px-8 py-4 rounded-xl transition-colors duration-200"
              :class="isDark ? 'bg-zinc-950/40 border border-zinc-900' : 'bg-white border border-gray-200'">
              <div class="text-center">
                <div class="text-3xl font-mono font-bold transition-colors duration-200"
                  :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'">
                  {{ 100 - lifetimeSolo.remaining }}
                </div>
                <div class="text-[10px] uppercase tracking-wider mt-1 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Places Restantes</div>
              </div>

              <div class="h-12 w-px transition-colors duration-200"
                :class="isDark ? 'bg-zinc-800' : 'bg-gray-300'"></div>

              <div class="text-center">
                <div class="text-3xl font-mono font-bold transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-gray-900'">
                  {{ lifetimeSolo.position || 0 }}
                </div>
                <div class="text-[10px] uppercase tracking-wider mt-1 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Votre Position</div>
              </div>

              <div class="h-12 w-px transition-colors duration-200"
                :class="isDark ? 'bg-zinc-800' : 'bg-gray-300'"></div>

              <div class="text-center">
                <div class="text-sm font-medium transition-colors duration-200"
                  :class="isDark ? 'text-red-400' : 'text-red-600'">
                  🔥 {{ ((lifetimeSolo.position - 1) / 100 * 100).toFixed(0) }}% Rempli
                </div>
                <div class="text-[10px] uppercase tracking-wider mt-1 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Dépêchez-vous !</div>
              </div>
            </div>
          </div>

          <div class="max-w-md mx-auto space-y-1">
            <div class="w-full h-2 rounded-full overflow-hidden transition-colors duration-200"
              :class="isDark ? 'bg-zinc-900' : 'bg-gray-200'">
              <div
                class="h-full transition-all duration-1000 ease-out"
                :class="isDark ? 'bg-gradient-to-r from-[#D5CCBC] to-amber-400' : 'bg-gradient-to-r from-amber-500 to-red-500'"
                :style="{ width: `${((lifetimeSolo.position - 1) / 100 * 100)}%` }"
              ></div>
            </div>
            <p class="text-[9px] font-mono text-center uppercase tracking-wider transition-colors duration-200"
              :class="isDark ? 'text-zinc-600' : 'text-gray-500'">
              Progression Globale • Tous Paliers Confondus
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto pt-4">

      <div class="border rounded-2xl p-6 flex flex-col relative transition-colors"
        :class="isDark
          ? 'bg-zinc-900/10 border-zinc-900 hover:border-zinc-800'
          : 'bg-white border-gray-200 hover:border-gray-300'">

        <div v-if="pricingMode === 'lifetime' && lifetimeSolo.isAvailable" class="flex flex-col h-full justify-between">
          <div class="space-y-5 flex-1">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium uppercase tracking-wider" :class="isDark ? 'text-white' : 'text-gray-900'">Solo Studio</h3>
              <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase" :class="isDark ? 'bg-amber-950/40 text-amber-400' : 'bg-amber-100 text-amber-700'">{{ lifetimeSolo.label }}</span>
            </div>
            <p class="text-[11px] font-light" :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Artisans indépendants & professionnels solos</p>

            <div class="flex items-baseline gap-1.5 border-b pb-4" :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
              <span class="text-3xl font-mono font-bold" :class="isDark ? 'text-white' : 'text-gray-900'">{{ lifetimeSolo.price }}€</span>
              <span class="text-xs uppercase tracking-wider" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">paiement unique à vie</span>
            </div>

            <ul class="space-y-3 py-2">
              <li v-for="item in ['1 Agenda Utilisateur Dédié', 'Prise de rendez-vous en ligne 24h/7j', '50 SMS de bienvenue offerts (Lancement)', 'Rappels automatiques synchronisés', 'Fiche établissement personnalisée']" :key="item" class="flex items-start gap-2.5 text-xs">
                <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="font-light" :class="isDark ? 'text-zinc-400' : 'text-gray-600'">{{ item }}</span>
              </li>
            </ul>
          </div>
          
          <div class="mt-6 pt-4 border-t" :class="isDark ? 'border-zinc-900/60' : 'border-gray-100'">
            <div class="text-[9px] uppercase tracking-wider mb-2" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">
              {{ lifetimeSolo.remaining }} places d'accès à vie restantes globales
            </div>
            <button @click="selectPlan('lifetime-solo')" :disabled="isProcessing" class="w-full py-3 rounded-xl text-xs font-medium uppercase tracking-widest transition-colors cursor-pointer disabled:opacity-50 focus:outline-none" :class="isDark ? 'bg-[#D5CCBC] text-zinc-950 hover:bg-white' : 'bg-amber-600 text-white hover:bg-amber-700'">
              🔥 Activer l'Accès à Vie
            </button>
          </div>
        </div>

        <!-- Monthly Solo -->
        <div v-if="pricingMode === 'monthly'" class="flex flex-col h-full justify-between">
          <div class="space-y-5 flex-1">
            <h3 class="text-sm font-medium uppercase tracking-wider" :class="isDark ? 'text-white' : 'text-gray-900'">Solo Studio</h3>
            <p class="text-[11px] font-light" :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Artisans indépendants & professionnels solos</p>

            <div class="flex items-baseline gap-1.5 border-b pb-4" :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
              <span class="text-3xl font-mono" :class="isDark ? 'text-white' : 'text-gray-900'">14,99€</span>
              <span class="text-xs uppercase tracking-wider" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">/ mois</span>
            </div>

            <ul class="space-y-3 py-2">
              <li v-for="item in ['1 Agenda Utilisateur Dédié', 'Prise de rendez-vous en ligne 24h/7j', '50 Crédits SMS inclus chaque mois', 'Rappels automatiques synchronisés']" :key="item" class="flex items-start gap-2.5 text-xs">
                <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" :class="isDark ? 'text-zinc-500' : 'text-gray-400'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="font-light" :class="isDark ? 'text-zinc-400' : 'text-gray-600'">{{ item }}</span>
              </li>
            </ul>
          </div>

          <button @click="selectPlan('solo')" :disabled="isProcessing" class="w-full mt-6 py-3 rounded-xl text-xs font-medium uppercase tracking-widest border transition-colors cursor-pointer disabled:opacity-50" :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800/50' : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-gray-300'">
            {{ isProcessing ? 'Chargement...' : 'Démarrer l\'abonnement' }}
          </button>
        </div>

        <!-- Lifetime not available message -->
        <div v-if="pricingMode === 'lifetime' && !lifetimeSolo.isAvailable" class="flex flex-col h-full justify-center items-center py-12">
          <i class="fas fa-lock text-4xl mb-4" :class="isDark ? 'text-zinc-700' : 'text-gray-300'"></i>
          <p class="text-sm font-medium" :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Offre à vie épuisée</p>
          <p class="text-xs mt-1" :class="isDark ? 'text-zinc-600' : 'text-gray-400'">Passez au mode Mensuel pour souscrire</p>
        </div>
      </div>

      <div class="border-2 rounded-2xl p-6 flex flex-col relative transition-colors duration-200"
        :class="isDark
          ? 'bg-zinc-900/30 border-[#D5CCBC]/30 shadow-lg shadow-black/40'
          : 'bg-amber-50 border-amber-300 shadow-lg shadow-amber-100'">
        <div class="absolute -top-3 right-6">
          <span class="px-3 py-1 text-[9px] font-medium uppercase tracking-widest rounded-md" :class="isDark ? 'bg-[#D5CCBC] text-zinc-950' : 'bg-amber-600 text-white'">Recommandé</span>
        </div>

        <div v-if="pricingMode === 'lifetime' && lifetimeEquipe.isAvailable" class="flex flex-col h-full justify-between">
          <div class="space-y-5 flex-1">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium uppercase tracking-wider" :class="isDark ? 'text-white' : 'text-gray-900'">Équipe Lab</h3>
              <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase" :class="isDark ? 'bg-amber-950/40 text-amber-400' : 'bg-amber-100 text-amber-700'">{{ lifetimeEquipe.label }}</span>
            </div>
            <p class="text-[11px] font-light" :class="isDark ? 'text-zinc-500' : 'text-gray-700'">Établissements comptant 2 à 3 collaborateurs</p>

            <div class="flex items-baseline gap-1.5 border-b pb-4" :class="isDark ? 'border-zinc-900' : 'border-amber-200'">
              <span class="text-3xl font-mono font-bold" :class="isDark ? 'text-white' : 'text-gray-900'">{{ lifetimeEquipe.price }}€</span>
              <span class="text-xs uppercase tracking-wider" :class="isDark ? 'text-zinc-600' : 'text-gray-600'">paiement unique à vie</span>
            </div>

            <ul class="space-y-3 py-2">
              <li v-for="item in ['Jusqu\'à 3 agendas collaborateurs', 'Attribution intelligente selon les spécialités', 'Gestion d\'offres spéciales & heures creuses', 'Fichier clients & Historique des No-Shows', '50 SMS de bienvenue offerts (Lancement)']" :key="item" class="flex items-start gap-2.5 text-xs">
                <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="font-light" :class="isDark ? 'text-zinc-300' : 'text-gray-700'">{{ item }}</span>
              </li>
            </ul>
          </div>

          <div class="mt-6 pt-4 border-t" :class="isDark ? 'border-zinc-900/60' : 'border-amber-200'">
            <div class="text-[9px] uppercase tracking-wider mb-2" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">
              {{ lifetimeSolo.remaining }} places d'accès à vie restantes globales
            </div>
            <button @click="selectPlan('lifetime-equipe')" :disabled="isProcessing" class="w-full py-3 rounded-xl text-xs font-medium uppercase tracking-widest transition-colors cursor-pointer disabled:opacity-50 focus:outline-none" :class="isDark ? 'bg-[#D5CCBC] text-zinc-950 hover:bg-white' : 'bg-amber-600 text-white hover:bg-amber-700'">
              🔥 Activer l'Accès à Vie
            </button>
          </div>
        </div>

        <!-- Monthly Équipe -->
        <div v-if="pricingMode === 'monthly'" class="flex flex-col h-full justify-between">
          <div class="space-y-5 flex-1">
            <h3 class="text-sm font-medium uppercase tracking-wider" :class="isDark ? 'text-white' : 'text-gray-900'">Équipe Lab</h3>
            <p class="text-[11px] font-light" :class="isDark ? 'text-zinc-500' : 'text-gray-700'">Établissements comptant 2 à 3 collaborateurs</p>

            <div class="flex items-baseline gap-1.5 border-b pb-4" :class="isDark ? 'border-zinc-900' : 'border-amber-200'">
              <span class="text-3xl font-mono" :class="isDark ? 'text-white' : 'text-gray-900'">29,99€</span>
              <span class="text-xs uppercase tracking-wider" :class="isDark ? 'text-zinc-600' : 'text-gray-600'">/ mois</span>
            </div>

            <ul class="space-y-3 py-2">
              <li v-for="item in ['Jusqu\'à 3 agendas collaborateurs', 'Attribution intelligente selon les spécialités', 'Gestion d\'offres spéciales & heures creuses', '50 Crédits SMS inclus chaque mois']" :key="item" class="flex items-start gap-2.5 text-xs">
                <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="font-light" :class="isDark ? 'text-zinc-300' : 'text-gray-700'">{{ item }}</span>
              </li>
            </ul>
          </div>

          <button @click="selectPlan('equipe')" :disabled="isProcessing" class="w-full mt-6 py-3 rounded-xl text-xs font-medium uppercase tracking-widest transition-colors cursor-pointer disabled:opacity-50 focus:outline-none" :class="isDark ? 'bg-[#D5CCBC] text-zinc-950 hover:bg-white' : 'bg-amber-600 text-white hover:bg-amber-700'">
            {{ isProcessing ? 'Chargement...' : 'Sélectionner l\'offre' }}
          </button>
        </div>

        <!-- Lifetime not available message -->
        <div v-if="pricingMode === 'lifetime' && !lifetimeEquipe.isAvailable" class="flex flex-col h-full justify-center items-center py-12">
          <i class="fas fa-lock text-4xl mb-4" :class="isDark ? 'text-zinc-700' : 'text-gray-300'"></i>
          <p class="text-sm font-medium" :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Offre à vie épuisée</p>
          <p class="text-xs mt-1" :class="isDark ? 'text-zinc-600' : 'text-gray-400'">Passez au mode Mensuel pour souscrire</p>
        </div>
      </div>

      <div class="border rounded-2xl p-6 flex flex-col relative transition-colors"
        :class="isDark
          ? 'bg-zinc-900/10 border-zinc-900 hover:border-zinc-800'
          : 'bg-white border-gray-200 hover:border-gray-300'">

        <div v-if="pricingMode === 'lifetime' && lifetimeSalon.isAvailable" class="flex flex-col h-full justify-between">
          <div class="space-y-5 flex-1">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium uppercase tracking-wider" :class="isDark ? 'text-white' : 'text-gray-900'">Salon Plus Cloud</h3>
              <span class="px-2 py-0.5 rounded text-[8px] font-bold uppercase" :class="isDark ? 'bg-amber-950/40 text-amber-400' : 'bg-amber-100 text-amber-700'">{{ lifetimeSalon.label }}</span>
            </div>
            <p class="text-[11px] font-light" :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Grands salons, réseaux & structures multi-agendas</p>

            <div class="flex items-baseline gap-1.5 border-b pb-4" :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
              <span class="text-3xl font-mono font-bold" :class="isDark ? 'text-white' : 'text-gray-900'">{{ lifetimeSalon.price }}€</span>
              <span class="text-xs uppercase tracking-wider" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">paiement unique à vie</span>
            </div>

            <ul class="space-y-3 py-2">
              <li v-for="item in ['De 4 à 8 agendas collaborateurs', 'Gestion avancée des plannings complexes', 'Outils de fidélisation & Statistiques', 'Support technique prioritaire dédié', '50 SMS de bienvenue offerts (Lancement)']" :key="item" class="flex items-start gap-2.5 text-xs">
                <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="font-light" :class="isDark ? 'text-zinc-400' : 'text-gray-600'">{{ item }}</span>
              </li>
            </ul>
          </div>

          <div class="mt-6 pt-4 border-t" :class="isDark ? 'border-zinc-900/60' : 'border-gray-100'">
            <div class="text-[9px] uppercase tracking-wider mb-2" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">
              {{ lifetimeSolo.remaining }} places d'accès à vie restantes globales
            </div>
            <button @click="selectPlan('lifetime-salon')" :disabled="isProcessing" class="w-full py-3 rounded-xl text-xs font-medium uppercase tracking-widest transition-colors cursor-pointer disabled:opacity-50 focus:outline-none" :class="isDark ? 'bg-[#D5CCBC] text-zinc-950 hover:bg-white' : 'bg-amber-600 text-white hover:bg-amber-700'">
              🔥 Activer l'Accès à Vie
            </button>
          </div>
        </div>

        <!-- Monthly Salon -->
        <div v-if="pricingMode === 'monthly'" class="flex flex-col h-full justify-between">
          <div class="space-y-5 flex-1">
            <h3 class="text-sm font-medium uppercase tracking-wider" :class="isDark ? 'text-white' : 'text-gray-900'">Salon Plus Cloud</h3>
            <p class="text-[11px] font-light" :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Grands salons, réseaux & structures multi-agendas</p>

            <div class="flex items-baseline gap-1.5 border-b pb-4" :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
              <span class="text-3xl font-mono" :class="isDark ? 'text-white' : 'text-gray-900'">49,99€</span>
              <span class="text-xs uppercase tracking-wider" :class="isDark ? 'text-zinc-600' : 'text-gray-500'">/ mois</span>
            </div>

            <ul class="space-y-3 py-2">
              <li v-for="item in ['De 4 à 8 agendas collaborateurs', 'Gestion avancée des plannings complexes', 'Outils de fidélisation & Statistiques', 'Support technique prioritaire dédié', '50 Crédits SMS inclus chaque mois']" :key="item" class="flex items-start gap-2.5 text-xs">
                <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" :class="isDark ? 'text-zinc-500' : 'text-gray-400'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="font-light" :class="isDark ? 'text-zinc-400' : 'text-gray-600'">{{ item }}</span>
              </li>
            </ul>
          </div>

          <button @click="selectPlan('salon')" :disabled="isProcessing" class="w-full mt-6 py-3 rounded-xl text-xs font-medium uppercase tracking-widest border transition-colors cursor-pointer disabled:opacity-50" :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800/50' : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-gray-300'">
            {{ isProcessing ? 'Chargement...' : 'Souscrire au plan' }}
          </button>
        </div>

        <!-- Lifetime not available message -->
        <div v-if="pricingMode === 'lifetime' && !lifetimeSalon.isAvailable" class="flex flex-col h-full justify-center items-center py-12">
          <i class="fas fa-lock text-4xl mb-4" :class="isDark ? 'text-zinc-700' : 'text-gray-300'"></i>
          <p class="text-sm font-medium" :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Offre à vie épuisée</p>
          <p class="text-xs mt-1" :class="isDark ? 'text-zinc-600' : 'text-gray-400'">Passez au mode Mensuel pour souscrire</p>
        </div>
      </div>

    </div>

    <div class="max-w-2xl mx-auto pt-10 space-y-4">
      <h3 class="text-xs font-medium uppercase tracking-widest text-center mb-6 transition-colors duration-200"
        :class="isDark ? 'text-white' : 'text-gray-900'">Informations complémentaires</h3>

      <div class="space-y-2">
        <details v-for="faq in faqs" :key="faq.q" class="border rounded-xl p-4 group transition-colors"
          :class="isDark
            ? 'bg-zinc-900/10 border-zinc-900 hover:border-zinc-800/60'
            : 'bg-white border-gray-200 hover:border-gray-300'">
          <summary class="text-xs font-medium cursor-pointer flex items-center justify-between list-none uppercase tracking-wide focus:outline-none transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-gray-700'">
            <span>{{ faq.q }}</span>
            <svg class="w-3 h-3 group-open:rotate-180 transition-all duration-200"
              :class="isDark ? 'text-zinc-600' : 'text-gray-400'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <p class="text-xs mt-3 leading-relaxed font-light m-0 transition-colors duration-200"
            :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
            {{ faq.a }}
          </p>
        </details>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import { useTheme } from '@/composables/useTheme'
import api from '@/api'

const { isDark } = useTheme()

const isProcessing = ref(false)
const pricingMode = ref('monthly') // 'monthly' or 'lifetime'

// Subscription status
const userSubscription = ref({
  hasActiveSubscription: false,
  type_tarification: null, // 'flexible' or 'lifetime'
  plan: null
})

const lifetimeSolo = ref({
  isAvailable: false,
  price: 0,
  tier: null,
  label: '',
  badge: '',
  position: 0,
  remaining: 0,
  percentFull: 0,
  tierProgress: 0,
  totalSlots: 100
})

const lifetimeEquipe = ref({
  isAvailable: false,
  price: 0,
  tier: null,
  label: '',
  badge: '',
  position: 0,
  remaining: 0,
  percentFull: 0,
  tierProgress: 0,
  totalSlots: 100
})

const lifetimeSalon = ref({
  isAvailable: false,
  price: 0,
  tier: null,
  label: '',
  badge: '',
  position: 0,
  remaining: 0,
  percentFull: 0,
  tierProgress: 0,
  totalSlots: 100
})

// Computed: Check if lifetime offers are blocked for this user
const isLifetimeBlocked = computed(() => {
  return userSubscription.value.hasActiveSubscription && userSubscription.value.type_tarification === 'flexible'
})

const faqs = ref([
  {
    q: "Puis-je faire évoluer mon offre à tout moment ?",
    a: "Oui. L'ajustement de votre offre s'effectue instantanément depuis vos configurations. La mise à niveau vers un forfait supérieur s'applique immédiatement, tandis que le passage à une formule inférieure prend effet au terme du cycle de facturation en cours."
  },
  {
    q: "L'accès à vie intègre-t-il les nouveautés à venir ?",
    a: "Absolument. La formule Partenaire Fondateur vous garantit la mise à disposition permanente de l'entièreté des innovations, modules et services déployés sur BookMySalon, de manière illimitée et sans surcoût."
  },
  {
    q: "Quelle est votre politique d'engagement ?",
    a: "Nos abonnements mensuels sont sans engagement de durée. Par ailleurs, toutes nos formules disposent d'un délai d'ajustement de 30 jours pour garantir l'adéquation de nos outils avec les besoins de votre établissement."
  },
  {
    q: "Comment fonctionne le système de paliers ?",
    a: "Le prix de l'accès à vie augmente par paliers de 30 places. Les Solo commencent au Palier 1 (199€), les Équipe au Palier 2 (299€), et les Salon au Palier 3 (499€). Si votre palier est complet, vous passez automatiquement au suivant. Le compteur est global : quand les 100 places sont vendues, l'offre disparaît définitivement."
  }
])

onMounted(async () => {
  await Promise.all([
    loadLifetimeOfferStatus(),
    loadUserSubscriptionStatus()
  ])
})

const loadUserSubscriptionStatus = async () => {
  try {
    const response = await api.subscription.getSubscriptionStatus()

    if (response.data && response.data.data && response.data.data.subscription) {
      const sub = response.data.data.subscription
      userSubscription.value = {
        hasActiveSubscription: sub.isActive || false,
        type_tarification: sub.type_tarification || null,
        plan: sub.plan || null
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement du statut d\'abonnement:', error)
  }
}

const loadLifetimeOfferStatus = async () => {
  try {
    // Charger les 3 paliers en parallèle
    const [soloRes, equipeRes, salonRes] = await Promise.all([
      api.subscription.getLifetimeTier('solo'),
      api.subscription.getLifetimeTier('equipe'),
      api.subscription.getLifetimeTier('salon')
    ])

    // Solo
    if (soloRes.data && soloRes.data.available) {
      lifetimeSolo.value = {
        isAvailable: soloRes.data.available,
        price: soloRes.data.price,
        tier: soloRes.data.tier,
        label: soloRes.data.label,
        badge: soloRes.data.badge,
        position: soloRes.data.position,
        remaining: soloRes.data.remaining,
        percentFull: soloRes.data.percentFull,
        tierProgress: soloRes.data.tierProgress || 0,
        totalSlots: 100
      }
    } else {
      lifetimeSolo.value.isAvailable = false
    }

    // Équipe
    if (equipeRes.data && equipeRes.data.available) {
      lifetimeEquipe.value = {
        isAvailable: equipeRes.data.available,
        price: equipeRes.data.price,
        tier: equipeRes.data.tier,
        label: equipeRes.data.label,
        badge: equipeRes.data.badge,
        position: equipeRes.data.position,
        remaining: equipeRes.data.remaining,
        percentFull: equipeRes.data.percentFull,
        tierProgress: equipeRes.data.tierProgress || 0,
        totalSlots: 100
      }
    } else {
      lifetimeEquipe.value.isAvailable = false
    }

    // Salon
    if (salonRes.data && salonRes.data.available) {
      lifetimeSalon.value = {
        isAvailable: salonRes.data.available,
        price: salonRes.data.price,
        tier: salonRes.data.tier,
        label: salonRes.data.label,
        badge: salonRes.data.badge,
        position: salonRes.data.position,
        remaining: salonRes.data.remaining,
        percentFull: salonRes.data.percentFull,
        tierProgress: salonRes.data.tierProgress || 0,
        totalSlots: 100
      }
    } else {
      lifetimeSalon.value.isAvailable = false
    }
  } catch (error) {
    console.error('Erreur lors du chargement des offres inaugurales :', error)
    lifetimeSolo.value.isAvailable = false
    lifetimeEquipe.value.isAvailable = false
    lifetimeSalon.value.isAvailable = false
  }
}

const selectPlan = async (planType) => {
  try {
    isProcessing.value = true
    console.log('📦 Sélection de la formule :', planType)

    const response = await api.subscription.createCheckoutSession(planType)

    if (response.data && response.data.url) {
      window.location.href = response.data.url
    } else {
      throw new Error('URL de paiement non reçue')
    }
  } catch (error) {
    console.error('Erreur lors de l\'initialisation de la session :', error)
    toast.error(error.response?.data?.message || 'Une erreur est survenue lors de l\'accès au portail de paiement.', {
      position: 'top-right',
      autoClose: 3000
    })
    isProcessing.value = false
  }
}
</script>

<style scoped>
/* Suppression du sélecteur natif pour conserver un design minimaliste */
summary::-webkit-details-marker {
  display: none;
}
</style>
